import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  FileText, Plus, Search, User, Calendar, Clock, AlertCircle,
  CheckCircle, Loader2, Stethoscope, Pill, Heart, Brain,
  Activity, ClipboardList, MessageSquare, Upload, Mic, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import VoiceNoteRecorder from './VoiceNoteRecorder';
import AIDocumentationAssist from './AIDocumentationAssist';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Note types that can be added to EHR
const NOTE_TYPES = [
  { value: 'clinical_note', label: 'Clinical Note', icon: <Stethoscope className="w-4 h-4" />, color: 'blue' },
  { value: 'nursing_note', label: 'Nursing Note', icon: <Heart className="w-4 h-4" />, color: 'pink' },
  { value: 'social_work_note', label: 'Social Work Note', icon: <User className="w-4 h-4" />, color: 'purple' },
  { value: 'chaplain_note', label: 'Chaplain/Spiritual Note', icon: <Heart className="w-4 h-4" />, color: 'amber' },
  { value: 'medication_note', label: 'Medication Note', icon: <Pill className="w-4 h-4" />, color: 'green' },
  { value: 'symptom_note', label: 'Symptom Assessment', icon: <Activity className="w-4 h-4" />, color: 'red' },
  { value: 'family_communication', label: 'Family Communication', icon: <MessageSquare className="w-4 h-4" />, color: 'cyan' },
  { value: 'care_coordination', label: 'Care Coordination', icon: <ClipboardList className="w-4 h-4" />, color: 'orange' },
  { value: 'cognitive_assessment', label: 'Cognitive Assessment', icon: <Brain className="w-4 h-4" />, color: 'indigo' },
  { value: 'general_note', label: 'General Note', icon: <FileText className="w-4 h-4" />, color: 'slate' },
];

const PRIORITY_OPTIONS = [
  { value: 'routine', label: 'Routine', color: 'slate' },
  { value: 'important', label: 'Important', color: 'yellow' },
  { value: 'urgent', label: 'Urgent', color: 'orange' },
  { value: 'critical', label: 'Critical', color: 'red' },
];

export default function AddToEHR({ 
  buttonVariant = 'default',
  buttonSize = 'default',
  buttonText = 'Add to EHR',
  showIcon = true,
  preSelectedPatient = null,
  preSelectedChartId = null,
  onSuccess = null,
  className = ''
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchingPatient, setSearchingPatient] = useState(false);
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    patientId: preSelectedPatient || '',
    chartId: preSelectedChartId || '',
    patientName: '',
    noteType: 'clinical_note',
    title: '',
    content: '',
    priority: 'routine',
    tags: [],
    attachments: []
  });
  
  const token = localStorage.getItem('token');

  const searchPatients = async (query) => {
    if (!query || query.length < 2) {
      setPatients([]);
      return;
    }
    
    setSearchingPatient(true);
    try {
      const response = await fetch(`${API_URL}/api/ehr/charts?search=${encodeURIComponent(query)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setPatients(data.charts || []);
    } catch (error) {
      console.error('Error searching patients:', error);
    } finally {
      setSearchingPatient(false);
    }
  };

  const selectPatient = (patient) => {
    const demo = patient.demographics || {};
    const name = `${demo.first_name || ''} ${demo.last_name || ''}`.trim();
    setFormData({
      ...formData,
      patientId: patient.patient_id || patient.id,
      chartId: patient.id,
      patientName: name
    });
    setPatients([]);
    setSearchQuery(name);
  };

  const handleSubmit = async () => {
    if (!formData.chartId) {
      toast.error('Please select a patient');
      return;
    }
    if (!formData.title.trim()) {
      toast.error('Please enter a note title');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Please enter note content');
      return;
    }
    
    setLoading(true);
    try {
      // Create the EHR note
      const response = await fetch(`${API_URL}/api/ehr/notes/add`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chart_id: formData.chartId,
          patient_id: formData.patientId,
          note_type: formData.noteType,
          title: formData.title,
          content: formData.content,
          priority: formData.priority,
          tags: formData.tags
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success('Note added to EHR successfully');
        setOpen(false);
        resetForm();
        if (onSuccess) onSuccess(data);
      } else {
        throw new Error(data.detail || 'Failed to add note');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to add note to EHR');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      patientId: preSelectedPatient || '',
      chartId: preSelectedChartId || '',
      patientName: '',
      noteType: 'clinical_note',
      title: '',
      content: '',
      priority: 'routine',
      tags: [],
      attachments: []
    });
    setSearchQuery('');
    setPatients([]);
  };

  const selectedNoteType = NOTE_TYPES.find(t => t.value === formData.noteType);

  return (
    <>
      <Button
        variant={buttonVariant}
        size={buttonSize}
        onClick={() => setOpen(true)}
        className={className}
        data-testid="add-to-ehr-button"
      >
        {showIcon && <Plus className="w-4 h-4 mr-2" />}
        {buttonText}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              Add to Clinical Record
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a note or documentation to the patient's EHR
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Patient Search */}
            {!preSelectedChartId && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Patient</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      searchPatients(e.target.value);
                    }}
                    placeholder="Search patient by name or MRN..."
                    className="bg-slate-700 border-slate-600 text-white pl-10"
                  />
                  {searchingPatient && (
                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-400 animate-spin" />
                  )}
                </div>
                
                {/* Search Results */}
                {patients.length > 0 && (
                  <div className="bg-slate-700 border border-slate-600 rounded-lg mt-1 max-h-48 overflow-y-auto">
                    {patients.map((patient) => {
                      const demo = patient.demographics || {};
                      const name = `${demo.first_name || ''} ${demo.last_name || ''}`.trim();
                      return (
                        <div
                          key={patient.id}
                          onClick={() => selectPatient(patient)}
                          className="px-4 py-2 hover:bg-slate-600 cursor-pointer flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <span className="text-white">{name || 'Unknown'}</span>
                          </div>
                          <span className="text-xs text-slate-400">
                            DOB: {demo.date_of_birth || 'N/A'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {formData.patientName && (
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className="bg-blue-500/20 text-blue-300">
                      <User className="w-3 h-3 mr-1" />
                      {formData.patientName}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFormData({ ...formData, patientId: '', chartId: '', patientName: '' });
                        setSearchQuery('');
                      }}
                      className="text-slate-400 hover:text-white h-6 px-2"
                    >
                      Change
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Note Type Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Note Type</label>
              <div className="grid grid-cols-2 gap-2">
                {NOTE_TYPES.map((type) => (
                  <Button
                    key={type.value}
                    variant={formData.noteType === type.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFormData({ ...formData, noteType: type.value })}
                    className={`justify-start ${
                      formData.noteType === type.value 
                        ? `bg-${type.color}-600 hover:bg-${type.color}-700` 
                        : 'border-slate-600 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {type.icon}
                    <span className="ml-2 text-xs">{type.label}</span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Title</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={`Enter ${selectedNoteType?.label || 'note'} title...`}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>

            {/* Content */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-300">Content</label>
                <div className="flex items-center gap-2">
                  {formData.chartId && (
                    <>
                      <VoiceNoteRecorder
                        chartId={formData.chartId}
                        patientName={formData.patientName}
                        onNoteCreated={() => {
                          setOpen(false);
                          resetForm();
                        }}
                        buttonVariant="outline"
                        buttonSize="sm"
                        className="border-cyan-600 text-cyan-400 hover:bg-cyan-600/20 h-7 text-xs"
                      />
                      <AIDocumentationAssist
                        chartId={formData.chartId}
                        patientName={formData.patientName}
                        onNoteGenerated={(suggestion) => {
                          setFormData({
                            ...formData,
                            content: suggestion.content,
                            title: suggestion.title || formData.title,
                            tags: [...new Set([...formData.tags, ...(suggestion.tags || [])])]
                          });
                        }}
                        buttonVariant="outline"
                        buttonSize="sm"
                        className="border-purple-600 text-purple-400 hover:bg-purple-600/20 h-7 text-xs"
                      />
                    </>
                  )}
                </div>
              </div>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Enter detailed note content..."
                className="bg-slate-700 border-slate-600 text-white min-h-[150px]"
                rows={6}
              />
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Priority</label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full bg-${opt.color}-500`} />
                        {opt.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Quick Tags */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Quick Tags</label>
              <div className="flex flex-wrap gap-2">
                {['Follow-up Required', 'Family Contacted', 'MD Notified', 'Pain Management', 'Symptom Change', 'Care Plan Update'].map((tag) => (
                  <Badge
                    key={tag}
                    variant={formData.tags.includes(tag) ? 'default' : 'outline'}
                    className={`cursor-pointer ${
                      formData.tags.includes(tag) 
                        ? 'bg-blue-500/20 text-blue-300' 
                        : 'border-slate-600 text-slate-400 hover:bg-slate-700'
                    }`}
                    onClick={() => {
                      if (formData.tags.includes(tag)) {
                        setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
                      } else {
                        setFormData({ ...formData, tags: [...formData.tags, tag] });
                      }
                    }}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Timestamp Info */}
            <div className="bg-slate-700/50 rounded-lg p-3 flex items-center gap-4 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date().toLocaleDateString()}
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {new Date().toLocaleTimeString()}
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Current User
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
                resetForm();
              }}
              className="border-slate-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || !formData.chartId || !formData.title || !formData.content}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Add to EHR
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
